<template lang="pug">
.LessonPlayerPage
  km-character(
    v-if="stage"
    :message="message"
    @onActionFinished="handleCharacterActionFinished"
  )
  km-step-indicator(
    class="indicator"
    :totalCount="sceneTotalCount"
    :currentCount="sceneCount"
  )
  .content
    .scene(
      v-if="stage"
      :style="sceneStyle"
    )
      .stepTitle
        | {{ sceneTitle || '   ' }}

      km-keyboard-input(
        :hidden="currentScene.keyboardHidden"
        :sceneId="`${sceneCount}`"
        :targetText="keyboardInputSettings.targetText"
        :targetKey="keyboardInputSettings.targetKey"
        :testRepeatCount="keyboardInputSettings.testRepeatCount"
        :isHandHidden="keyboardInputSettings.isHandHidden"
        :handType="keyboardInputSettings.handType"
        :isHomePositionVisibled="keyboardInputSettings.isHomePositionVisibled"
        :targetColorEnabled="keyboardInputSettings.targetColorEnabled"
        @onSpaceKeyDown="handleSpaceKeyDown"
        @onInputFinished="handleInputFinished"
      )
      .image(v-if="currentScene.imageUrl")
        img(:src="currentScene.imageUrl")
      .movie(v-if="isMovieEnabled")
        video.video(:src="currentScene.movieSettings.path" controls preload ref="movieRef")
      .wipeMovie(v-if="currentScene.wipeMovieSettings && currentScene.wipeMovieSettings.path")
        video.video(
          controls
          preload
          :src="currentScene.wipeMovieSettings.path"
          :autoplay="currentScene.wipeMovieSettings.playAfterSpeechFinished ? null : 'autoplay'"
          :muted="currentScene.wipeMovieSettings.muted ? 'muted' : null"
          ref="wipeMovieRef"
        )

  .skipRecommendButton(
    v-show="isSceneFinished"
    @click="handleSkipButtonClick"
    v-menuHoverSound
    v-menuClickSound
  )
    | 次へ (スペースキー)

  km-loading-overlay(
    v-if="assetLoaded === false"
    :queuedAssets="assets"
    :loadedAssets="loadedAssets"
  )

  km-lesson-footer.footer(
    :navigationHidden="embedded"
    :title="title"
    :areaId="stage && stage.areaId"
    :stageId="stage && stage.stageId"
    backPath="/lesson/select"
  )

  transition(name="fade")
    .Overlay(v-if="isOpening")
      .inner
        .title {{ stage.title }}

  transition(name="fade")
    .Overlay.Overlay--clear(v-if="isEnding")
      .inner
        .title {{ stage.title }} クリア！

  .debug(v-if="constants.IS_DEBUG")
    .forceEndButton(@click="handleForceEndButtonClick") 強制クリア

</template>

<script>
import * as _ from 'lodash'
import { mapState, mapActions } from 'vuex'
import { VueTyper } from 'vue-typer'
import soundService from '@/services/SoundService'
import { delay } from '@/helper'
import LessonFooter from '@/components/shared/LessonFooter'
import StepIndicator from '@/components/shared/StepIndicator'
import KeyboardInput from '@/components/shared/KeyboardInput'
import Character from '@/components/shared/Character'
import * as constants from '@/constants'

export default {
  name: 'lesson-player-page',
  components: {
    'vue-typer': VueTyper,
    'km-lesson-footer': LessonFooter,
    'km-step-indicator': StepIndicator,
    'km-keyboard-input': KeyboardInput,
    'km-character': Character
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      constants,
      assetLoaded: null,
      assets: [
        { type: 'image', path: require('@/assets/logo.svg') },
        { type: 'sound', path: '/app/static/sounds/bgm_lesson.mp3' }
      ],
      isOpening: false,
      isEnding: false,

      isSceneFinished: false,
      isInputFinished: false,

      stage: null,
      sceneIndex: 0,
      sceneCount: 0,

      sceneTitle: '',
      message: '',
      keyboardInputSettings: {},

      nextSceneTimer: null
    }
  },
  watch: {
    sceneIndex() {
      this.initScene()
    },
    async soundSettingFinished() {
      if (this.soundSettingFinished) {
        await this.load()
        await this.startOpening()
      }
    },
    isMovieEnabled() {
      setTimeout(() => {
        if (this.isMovieEnabled) {
          if (this.$refs.movieRef) {
            this.$refs.movieRef.addEventListener('play', this.handleMoviePlayed)
            this.$refs.movieRef.addEventListener('pause', this.handleMoviePaused)
          }
        }
      })
    }
  },
  computed: {
    ...mapState('App', {
      soundSettingFinished: state => state.soundSettingFinished
    }),
    ...mapState('User', {
      progress: state => state.progress,
      exp: state => state.exp
    }),
    ...mapState('Assets', {
      loadedAssets: state => state.assets
    }),
    title: function() {
      if (this.embedded) {
        return this.stage && this.stage.title
      } else {
        return (
          this.stage &&
          `${this.stage.areaId}-${this.stage.stageId} ${this.stage.title}`
        )
      }
    },
    currentScene: function() {
      return this.stage ? this.stage.scenes[this.sceneIndex] : {}
    },
    sceneTotalCount: function() {
      return this.stage && this.stage.scenes
        ? _.reject(this.stage.scenes, { type: 'feedback' }).length
        : 1
    },
    sceneStyle: function() {
      const style = {}
      const backgroundImagePath = _.get(this.stage, 'background.paths.normal')
      if (backgroundImagePath) {
        style.backgroundImage = `url(${backgroundImagePath})`
      }
      return style
    },
    isMovieEnabled() {
      return Boolean(this.currentScene.movieSettings)
    }
  },
  methods: {
    ...mapActions('Sound', ['playBGM', 'stopBGM']),
    ...mapActions('User', ['addExperience', 'addProgress']),
    ...mapActions('Assets', ['fetchAssets', 'isAssetLoaded']),
    async load() {
      try {
        const { areaId, stageId, lessonId } = this.$route.params
        this.assetLoaded = await this.isAssetLoaded(
          `lesson${this.embedded ? lessonId : ''}`
        )
        const settings = await (
          await fetch('/app/static/json/settings.json', { cache: 'no-store' })
        ).json()
        const url = this.embedded
          ? `/app/static/json/lesson/${settings.lessons[lessonId]}.json`
          : `/app/static/json/basicLesson/lesson_area_${areaId}/basic_${areaId}_${stageId}.json`
        const response = await fetch(url, { cache: 'no-store' }).catch(() => {
          alert('問題読み込みエラー')
          if (!this.embedded) this.$router.push('/')
        })
        this.stage = await response.json()
        if (this.stage.background) {
          Object.keys(this.stage.background.paths).forEach(key => {
            this.assets.push({
              type: 'image',
              path: this.stage.background.paths[key]
            })
          })
        }
        if (this.stage.character) {
          Object.keys(this.stage.character.imagePaths).forEach(key => {
            this.assets.push({
              type: 'image',
              path: this.stage.character.imagePaths[key]
            })
          })
        }
        await this.fetchAssets({
          currentPage: `lesson${this.embedded ? lessonId : ''}`,
          assets: this.assets
        })
        setTimeout(() => {
          this.assetLoaded = true
        }, 500)
      } catch (e) {
        alert('読み込みエラー')
        this.$router.push('/')
      }
    },
    async startOpening() {
      this.isOpening = true
      this.playBGM('bgmLesson')
      await delay(1500)
      this.isOpening = false
      this.initScene()
    },
    playWipeMovie() {
      this.$refs.wipeMovieRef.play()
    },
    playMovie() {
      this.$refs.movieRef.play()
    },
    setLevel() {
      // const isDing = this.exp + this.stage.exp >= 100
      this.addExperience(this.stage.exp)
    },
    setProgress() {
      if (this.embedded) return
      // 既にクリアしている場合は更新しない
      const index = _.findIndex(this.progress, {
        id: this.stage.id
      })
      if (index !== -1) return
      this.addProgress({
        id: this.stage.id,
        category: this.stage.category,
        area: this.stage.areaId,
        stage: this.stage.stageId,
        status: constants.PROGRESS_STATUS.COMPLETED
      })
    },
    async startEnding() {
      this.isEnding = true
      setTimeout(() => {
        soundService.play('clear')
      }, 300)
      this.setLevel()
      this.setProgress()
      await delay(1500)
      // スキップされていた場合を考慮
      if (
        this.$route.name === 'lesson-player-page' ||
        this.$route.name === 'embedded-lesson-player-page'
      ) {
        this.$router.push({
          path: this.stage.endRedirectPath
            ? this.stage.endRedirectPath
            : '/lesson/select',
          query: {
            type: 'finished',
            areaId: this.stage.areaId,
            stageId: this.stage.stageId
          }
        })
      }
    },
    setKeyboardSettings(keyboardInputSettings) {
      this.keyboardInputSettings = {}
      this.$set(
        this.keyboardInputSettings,
        'targetText',
        keyboardInputSettings.targetText
      )
      this.$set(
        this.keyboardInputSettings,
        'targetKey',
        keyboardInputSettings.targetKey
      )
      this.$set(
        this.keyboardInputSettings,
        'testRepeatCount',
        keyboardInputSettings.testRepeatCount
      )
      this.$set(
        this.keyboardInputSettings,
        'handType',
        keyboardInputSettings.handType
      )
      this.$set(
        this.keyboardInputSettings,
        'isHandHidden',
        keyboardInputSettings.isHandHidden
      )
      this.$set(
        this.keyboardInputSettings,
        'isHomePositionVisibled',
        keyboardInputSettings.isHomePositionVisibled
      )
      this.$set(
        this.keyboardInputSettings,
        'targetColorEnabled',
        keyboardInputSettings.targetColorEnabled
      )
    },
    initScene() {
      this.isSceneFinished = Boolean(this.currentScene.skippable)
      if (this.currentScene.title) this.sceneTitle = this.currentScene.title
      if (this.currentScene.message || this.currentScene.message === '')
        this.message = this.currentScene.message
      switch (this.currentScene.type) {
        case 'singleKeyTest':
        case 'textTest':
          if (this.currentScene.keyboardInputSettings) {
            this.setKeyboardSettings(this.currentScene.keyboardInputSettings)
          }
          this.sceneCount += 1
          break
        case 'movie':
          this.sceneCount += 1
          break
        case 'feedback':
          this.isSceneFinished = true
      }
    },
    nextScene() {
      if (this.isEnding) return
      clearTimeout(this.nextSceneTimer)
      this.nextSceneTimer = null

      if (this.isMovieEnabled) {
        this.$refs.movieRef.removeEventListener('play', this.handleMoviePlayed)
        this.$refs.movieRef.removeEventListener('pause', this.handleMoviePaused)
      }

      const isStageFinished = _.isEmpty(this.stage.scenes[this.sceneIndex + 1])
      if (isStageFinished) {
        this.startEnding()
        return
      }
      this.sceneIndex += 1
    },
    handleOKButtonClick() {
      this.sceneIndex += 1
    },
    handleSpaceKeyDown() {
      if (!this.isSceneFinished) return
      this.nextScene()
    },
    handleInputFinished() {
      this.isInputFinished = true
      this.isSceneFinished = true
      this.nextSceneTimer = setTimeout(() => {
        this.nextScene()
      }, this.currentScene.endDelay || 1000)
    },
    handleSkipButtonClick() {
      if (!this.isSceneFinished) return
      this.nextScene()
    },
    handleCharacterActionFinished() {
      if (this.currentScene.type === 'feedback') {
        this.nextSceneTimer = setTimeout(() => {
          this.nextScene()
        }, this.currentScene.endDelay || 1000)
      } else {
        const playWipeAfterSpeechFinished = _.get(
          this.currentScene,
          'wipeMovieSettings.playAfterSpeechFinished'
        )
        if (playWipeAfterSpeechFinished) this.playWipeMovie()

        const playAfterSpeechFinished = _.get(
          this.currentScene,
          'movieSettings.playAfterSpeechFinished'
        )
        if (playAfterSpeechFinished) this.playMovie()
      }
    },
    handleForceEndButtonClick() {
      this.startEnding()
    },
    handleMoviePlayed() {
      this.stopBGM()
      this.isSceneFinished = true
    },
    handleMoviePaused() {
      this.playBGM('bgmLesson')
    }
  },
  async mounted() {
    if (!this.embedded) {
      // 埋め込み以外はSoundSetting済み
      await this.load()
      await this.startOpening()
    }

    // リロードしても喋り続ける不具合の対策
    window.onbeforeunload = function() {
      soundService.stopSpeech()
      return null
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteUpdate(to, from, next) {
    next()
  },
  beforeDestroy() {
    soundService.stop('bgmLesson')
    soundService.stopSpeech()
  },
  destroyed() {}
}
</script>

<style lang="scss" scoped>
.LessonPlayerPage {
  height: 100%;
  width: 100%;
}

.content {
  padding: 0;
  height: 100%;
  text-align: center;
  overflow: hidden;

  .scene {
    height: 100%;
    background-size: cover;
    background-position: center center;

    .stepTitle {
      margin: 0 0 35px 0;
      padding: 30px;
      height: 100px;
      background: rgba(255, 255, 255, 0.8);
      color: #000;
      font-size: 3rem;
    }

    .movie {
      .video {
        width: 60%;
        height: 100%;
        border: 2px solid #000;
      }
    }

    .wipeMovie {
      position: absolute;
      left: 30px;
      bottom: 30px;
      z-index: 1000;
      .video {
        width: 320px;
        border: 2px solid #000;
        border-radius: 5px;
      }
    }
  }
}

.indicator {
  position: absolute;
  display: flex;
  bottom: 68px;
  left: 30%;
  z-index: 50;
}

.Overlay {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 101;

  .inner {
    display: flex;
    width: 100%;
    height: 100px;
    background: rgba(#fff, 0.95);
    color: #000;
    font-size: 3rem;
    align-items: center;
    justify-content: center;
  }

  .title {
  }

  &--clear {
    .inner {
      background: rgba(yellow, 0.95);
      color: #000;
    }
  }
}

.skipRecommendButton {
  position: absolute;
  left: calc(50% - 220px);
  bottom: 75px;
  margin: 25px;
  padding: 10px 40px;
  width: 400px;
  border: 3px solid #000;
  background: #fff;
  color: #000;
  font-size: 2rem;
  text-align: center;
  border-radius: 10px;
  z-index: 100;
  animation-name: blink;
  animation-duration: 0.7229s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  transition: ease 0.5s;
  transition-property: width, left;

  @keyframes blink {
    0% {
    }

    100% {
    }
  }

  &:hover {
    left: calc(50% - 245px);
    width: 450px;
  }
}

.footer {
  position: absolute;
  bottom: 0;
}

.debug {
  position: fixed;
  left: 0;
  bottom: 0;
  color: #fff;
}
</style>
